import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coins, Calculator, TrendingUp } from 'lucide-react';
import { usePricingRates } from '@/hooks/usePricingRates';
import { creditsToMinutes } from '@/utils/creditConversionUtils';

interface CreditCalculatorProps {
  durationMinutes: number;
  activityType?: 'audio_generation' | 'shorts_generation';
  showDetails?: boolean;
  className?: string;
}

export const CreditCalculator: React.FC<CreditCalculatorProps> = ({
  durationMinutes,
  activityType = 'audio_generation',
  showDetails = true,
  className = ''
}) => {
  const { rates, audioRate, shortsRate, userType, tier } = usePricingRates();

  if (!rates) {
    return null;
  }

  const calculateCredits = (): number => {
    if (activityType === 'shorts_generation') {
      return shortsRate;
    }
    // Round up for partial minutes
    return Math.ceil(durationMinutes * audioRate);
  };

  const creditsRequired = calculateCredits();
  const rate = activityType === 'audio_generation' ? audioRate : shortsRate;

  const getUserTypeDisplay = () => {
    return userType.charAt(0).toUpperCase() + userType.slice(1);
  };

  const getTierDisplay = () => {
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const isDiscounted = tier === 'pro' && audioRate < 2; // Pro tiers get discounts

  return (
    <Card className={`${className} border-primary/20 bg-gradient-to-br from-primary/5 to-primary/10`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-primary/20">
              <Calculator className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Estimated Cost</p>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <p className="text-2xl font-bold flex items-center gap-1">
                    {creditsRequired}
                    <Coins className="h-5 w-5 text-primary" />
                  </p>
                  {activityType === 'audio_generation' && (
                    <p className="text-xs text-muted-foreground">
                      for {durationMinutes} min session
                    </p>
                  )}
                </div>
                {isDiscounted && (
                  <Badge variant="default" className="text-xs">
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Pro Discount
                  </Badge>
                )}
              </div>
            </div>
          </div>

          {showDetails && activityType === 'audio_generation' && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                {durationMinutes} min × {rate} credits/min
              </p>
              <div className="flex items-center justify-end gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {getUserTypeDisplay()}
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {getTierDisplay()}
                </Badge>
              </div>
            </div>
          )}

          {showDetails && activityType === 'shorts_generation' && (
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                1 short = {rate} credit{rate !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center justify-end gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {getUserTypeDisplay()}
                </Badge>
              </div>
            </div>
          )}
        </div>

        {isDiscounted && showDetails && (
          <div className="mt-3 pt-3 border-t border-primary/20">
            <p className="text-xs text-muted-foreground">
              You're saving {Math.round((1 - rates.overage_multiplier) * 100)}% with your Pro tier subscription
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
